import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Colonia } from '../types/colonia';
import {
  CheckoutPaymentPayload,
  CheckoutPaymentResult,
  VagasInscricao,
  confirmarPagamentoInscricao,
  consultarPagamento,
  consultarVagas,
  criarInscricao,
  criarPagamento,
  notificarNovaInscricao
} from './formularioParts/api';
import { ContratoModal, GratuitoModal } from './formularioParts/components/ContratoModal';
import { PaymentCheckout } from './formularioParts/components/PaymentCheckout';
import { ProgressSteps } from './formularioParts/components/ProgressSteps';
import { etapasAtivas, exibirAdicionais, initialForm } from './formularioParts/constants';
import { AdicionaisStep } from './formularioParts/sections/AdicionaisStep';
import { CamisaStep } from './formularioParts/sections/CamisaStep';
import { ConsentimentoStep } from './formularioParts/sections/ConsentimentoStep';
import { DadosCriancaStep } from './formularioParts/sections/DadosCriancaStep';
import { EnderecoStep } from './formularioParts/sections/EnderecoStep';
import { InformacoesStep } from './formularioParts/sections/InformacoesStep';
import { ResponsaveisStep } from './formularioParts/sections/ResponsaveisStep';
import { RetiradaStep } from './formularioParts/sections/RetiradaStep';
import { EtapaStatus, FormState, PessoaRetiradaForm } from './formularioParts/types';
import { calcularIdade, fileToCompressedDataUrl, formatCep, formatDate, formatPhone, onlyNumbers, turmaPorIdade } from './formularioParts/utils';

const Formulario: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [etapaAtual, setEtapaAtual] = useState(1);
  const [pessoasRetirada, setPessoasRetirada] = useState<PessoaRetiradaForm[]>([]);
  const [showContratoModal, setShowContratoModal] = useState(false);
  const [showGratuito, setShowGratuito] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [cepErro, setCepErro] = useState('');
  const [telefoneErro, setTelefoneErro] = useState('');
  const [assinaturaDesenhada, setAssinaturaDesenhada] = useState(false);
  const [assinaturaErro, setAssinaturaErro] = useState('');
  const [assinaturaUrl, setAssinaturaUrl] = useState('');
  const [valorFinalCustom, setValorFinalCustom] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutPayment, setCheckoutPayment] = useState<CheckoutPaymentResult | null>(null);
  const [registrationId, setRegistrationId] = useState('');
  const [vagas, setVagas] = useState<VagasInscricao | null>(null);

  useEffect(() => {
    let ativo = true;
    consultarVagas().then((resultado) => {
      if (ativo) setVagas(resultado);
    });
    return () => {
      ativo = false;
    };
  }, []);

  const valorInscricao = form.associadoUba ? 220 : 300;
  const valorAdicionais = exibirAdicionais
    ? (form.transporteVan ? 150 : 0) + (form.participacaoBingo ? 30 : 0)
    : 0;
  const valorTotal = form.gratuito ? 0 : valorFinalCustom ?? valorInscricao + valorAdicionais;

  const updateForm = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const etapaCompleta = useMemo<EtapaStatus>(() => {
    const telefoneResponsavelValido = onlyNumbers(form.telefoneMae).length >= 10;
    return {
      1: !!form.fotoUrl && !!form.nomeCompleto.trim() && !!form.dataNascimento && (!form.associadoUba || !!form.codigoAssociado.trim()),
      2: !!form.cep && !!form.endereco.trim() && !!form.numero.trim() && !!form.bairro.trim() && !!form.cidade.trim() && !!form.uf,
      3: !!form.tamanhoCamisa,
      4: !!form.nomeMae.trim() && telefoneResponsavelValido,
      5: !!form.nomeRetirada.trim() && !!form.parentescoRetirada.trim() && !!form.fotoUrlRetirada && pessoasRetirada.every((pessoa) => pessoa.nome && pessoa.parentesco && pessoa.fotoUrl),
      6: true,
      7: true,
      8: form.consentimentoLido && assinaturaDesenhada
    };
  }, [assinaturaDesenhada, form, pessoasRetirada]);

  const input = (field: keyof FormState) => ({
    value: String(form[field] ?? ''),
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      updateForm(field as never, event.target.value as never)
  });

  const handlePhoto = async (event: ChangeEvent<HTMLInputElement>, callback: (url: string) => void) => {
    const file = event.target.files?.[0];
    if (!file) return;
    callback(await fileToCompressedDataUrl(file));
    event.target.value = '';
  };

  const handleDataNascimento = (value: string) => {
    const dataNascimento = formatDate(value);
    const idade = calcularIdade(dataNascimento);
    setForm((current) => ({
      ...current,
      dataNascimento,
      idade,
      turma: idade ? turmaPorIdade(idade) : ''
    }));
  };

  const handleCotaChange = (value: string) => {
    const normalized = value.toLowerCase().trim();
    setShowGratuito(normalized === 'gratuito');
    setValorFinalCustom(normalized === 'valor200' ? 200 : null);
    setForm((current) => ({
      ...current,
      codigoAssociado: value,
      gratuito: normalized === 'gratuito'
    }));
  };

  const placeholderImage = (label: string, background: string) => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="640"><rect width="100%" height="100%" fill="${background}"/><circle cx="320" cy="245" r="120" fill="#fff" opacity=".92"/><path d="M120 590c22-142 108-220 200-220s178 78 200 220" fill="#fff" opacity=".92"/><text x="320" y="620" text-anchor="middle" font-family="Arial" font-size="30" font-weight="700" fill="#234">${label}</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const placeholderSignature = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="350" height="120"><rect width="100%" height="100%" fill="white"/><path d="M28 82c38-62 42 30 76-23 20-31 21 28 48 3 22-20 34 20 60 0 20-16 38 9 92-16" fill="none" stroke="black" stroke-width="4" stroke-linecap="round"/><text x="190" y="103" font-family="cursive" font-size="18">Responsável</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const autocompletarFormulario = () => {
    const assinaturaTeste = placeholderSignature();
    setForm({
      ...initialForm,
      fotoUrl: placeholderImage('PARTICIPANTE', '#8ecae6'),
      associadoUba: false,
      nomeCompleto: 'João Silva Santos',
      dataNascimento: '15/03/2015',
      idade: calcularIdade('15/03/2015'),
      turma: turmaPorIdade(calcularIdade('15/03/2015')),
      cep: '36500-001',
      endereco: 'Rua São José',
      numero: '100',
      complemento: 'Casa',
      bairro: 'Centro',
      cidade: 'Ubá',
      uf: 'MG',
      telefoneResidencial: '(32) 3531-0000',
      tamanhoCamisa: '10',
      nomeMae: 'Maria Silva Santos',
      telefoneMae: '(32) 99999-1111',
      telefoneAdicionalMae: '(32) 3531-1111',
      nomePai: 'Carlos Santos',
      telefonePai: '(32) 99999-2222',
      nomeRetirada: 'Ana Souza',
      parentescoRetirada: 'Tia',
      fotoUrlRetirada: placeholderImage('RETIRADA', '#ffb703'),
      sabeNadar: true,
      autorizacaoPiscina: 'autorizado',
      possuiAlergia: false,
      possuiDoenca: false,
      tomaMedicamento: false,
      medicacaoHiperatividade: false,
      transporteVan: exibirAdicionais,
      participacaoBingo: exibirAdicionais,
      consentimentoLido: true
    });
    setPessoasRetirada([]);
    setAssinaturaUrl(assinaturaTeste);
    setAssinaturaDesenhada(true);
    setAssinaturaErro('');
    setTelefoneErro('');
    setCepErro('');
    setEtapaAtual(8);
    window.scrollTo(0, 0);
  };

  const handleNomeChange = (value: string) => {
    if (value.trim().toLowerCase() === 'autocompletar') {
      autocompletarFormulario();
      return;
    }
    updateForm('nomeCompleto', value);
  };

  const buscarCep = async (value: string) => {
    const cep = formatCep(value);
    updateForm('cep', cep);
    setCepErro('');
    const numbers = onlyNumbers(cep);
    if (numbers.length !== 8) return;

    try {
      setBuscandoCep(true);
      const response = await fetch(`https://viacep.com.br/ws/${numbers}/json/`);
      const data = await response.json();
      if (data.erro) {
        setCepErro('CEP não encontrado.');
        return;
      }
      setForm((current) => ({
        ...current,
        cep,
        endereco: data.logradouro || current.endereco,
        bairro: data.bairro || current.bairro,
        cidade: data.localidade || current.cidade,
        uf: data.uf || current.uf
      }));
    } catch {
      setCepErro('Erro ao buscar CEP. Preencha manualmente.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const validarTelefoneMae = (value: string) => {
    const telefone = formatPhone(value);
    updateForm('telefoneMae', telefone);
    setTelefoneErro(onlyNumbers(telefone).length >= 10 ? '' : 'Telefone inválido');
  };

  const proximaEtapa = () => {
    if (!etapaCompleta[etapaAtual]) return;
    const currentIndex = etapasAtivas.findIndex((etapa) => etapa.numero === etapaAtual);
    const next = etapasAtivas[Math.min(etapasAtivas.length - 1, currentIndex + 1)].numero;
    setEtapaAtual(next);
    window.scrollTo(0, 0);
    if (next === 8) setTimeout(() => setShowContratoModal(true), 300);
  };

  const etapaAnterior = () => {
    setEtapaAtual((current) => {
      const currentIndex = etapasAtivas.findIndex((etapa) => etapa.numero === current);
      return etapasAtivas[Math.max(0, currentIndex - 1)].numero;
    });
    window.scrollTo(0, 0);
  };

  const irParaEtapa = (step: number) => {
    if (!etapasAtivas.some((etapa) => etapa.numero === step)) return;
    setEtapaAtual(step);
    window.scrollTo(0, 0);
    if (step === 8) setTimeout(() => setShowContratoModal(true), 300);
  };

  const updatePessoaRetirada = (id: string, patch: Partial<PessoaRetiradaForm>) => {
    setPessoasRetirada((current) => current.map((pessoa) => (pessoa.id === id ? { ...pessoa, ...patch } : pessoa)));
  };

  const buildInscricao = (payment?: CheckoutPaymentResult, couponFree = false): Colonia => ({
    ...form,
    pessoasRetirada,
    valorInscricao,
    valorAdicionais,
    assinaturaUrl,
    dataConsentimento: new Date(),
    gratuito: form.gratuito || couponFree,
    status: payment && ['CONFIRMED', 'RECEIVED'].includes(payment.status) ? 'confirmado' : form.gratuito || couponFree ? 'confirmado' : 'pendente',
    pago: form.gratuito || couponFree || Boolean(payment && ['CONFIRMED', 'RECEIVED'].includes(payment.status)),
    paymentMethod: form.gratuito || couponFree ? 'dinheiro' : payment?.billingType === 'CREDIT_CARD' ? 'credit_card' : 'pix',
    asaasPaymentId: couponFree ? 'GRATUIDADE_CUPOM' : form.gratuito ? 'GRATUITO' : payment?.id,
    invoiceUrl: payment?.invoiceUrl,
    installmentCount: payment?.installmentCount || 1,
    installmentValue: payment?.installmentValue || payment?.value,
    valorTotal: couponFree ? 0 : payment?.value || valorTotal
  });

  const triggerRegistrationMessages = async (registrationId: string) => {
    try {
      await notificarNovaInscricao(registrationId);
    } catch (error) {
      console.warn('[WHATSAPP] Inscrição salva, mas as mensagens não foram enviadas.', error);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!form.consentimentoLido) {
      setShowContratoModal(true);
      return;
    }
    if (!assinaturaDesenhada) {
      setAssinaturaErro('Você deve desenhar sua assinatura');
      return;
    }

    if (!form.gratuito) {
      setCheckoutOpen(true);
      return;
    }

    try {
      setLoading(true);
      const inscricao = await criarInscricao(buildInscricao());
      if (!inscricao.id) throw new Error('A inscrição foi salva sem um identificador.');
      await triggerRegistrationMessages(inscricao.id);
      navigate(`/pagamento-sucesso/${encodeURIComponent(inscricao.id)}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Erro ao salvar inscrição.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutPayment = async (payload: CheckoutPaymentPayload) => {
    try {
      setLoading(true);
      setSubmitError('');
      let savedId = registrationId;
      if (!savedId) {
        const inscricao = await criarInscricao(buildInscricao());
        savedId = inscricao.id || '';
        if (!savedId) throw new Error('A inscrição foi salva sem um identificador.');
        setRegistrationId(savedId);
      }
      const payment = await criarPagamento({
        ...payload,
        registrationReference: `COLONIA-${savedId}`
      });
      setCheckoutPayment(payment);
      await confirmarPagamentoInscricao(savedId, payment);
      if (['CONFIRMED', 'RECEIVED'].includes(payment.status)) await triggerRegistrationMessages(savedId);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Erro ao processar pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const handleCouponFreeRegistration = async (cupom: string) => {
    try {
      setLoading(true);
      setSubmitError('');
      const inscricao = await criarInscricao({ ...buildInscricao(undefined, true), cupomUtilizado: cupom });
      if (!inscricao.id) throw new Error('A inscrição foi salva sem um identificador.');
      await triggerRegistrationMessages(inscricao.id);
      navigate(`/pagamento-sucesso/${encodeURIComponent(inscricao.id)}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Erro ao finalizar a inscrição gratuita.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckPayment = async () => {
    if (!checkoutPayment) return;
    try {
      setLoading(true);
      setSubmitError('');
      const payment = await consultarPagamento(checkoutPayment.id);
      setCheckoutPayment(payment);
      if (registrationId) await confirmarPagamentoInscricao(registrationId, payment);
      if (['CONFIRMED', 'RECEIVED'].includes(payment.status)) {
        if (!registrationId) throw new Error('Não foi possível localizar a inscrição confirmada.');
        await triggerRegistrationMessages(registrationId);
        navigate(`/pagamento-sucesso/${encodeURIComponent(registrationId)}`);
      } else if (payment.billingType === 'CREDIT_CARD') {
        setSubmitError('O pagamento ainda está em análise. Aguarde alguns instantes e consulte novamente.');
      } else {
        setSubmitError('O PIX ainda não foi identificado. Aguarde alguns segundos e tente novamente.');
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Erro ao consultar pagamento.');
    } finally {
      setLoading(false);
    }
  };

  const commonStepProps = { form, etapaCompleta, updateForm, onPrev: etapaAnterior, onNext: proximaEtapa, input };

  if (vagas?.encerrado) {
    return (
      <div className="uba-form-page">
        <div className="uba-container">
          <header className="uba-header">
            <img src="/logo-compact.webp" alt="Colônia de Férias - UBA Clube" className="uba-header-logo" />
          </header>
          <section className="inscricoes-encerradas">
            <span className="inscricoes-encerradas-selo">Inscrições encerradas</span>
            <h2>Todas as vagas foram preenchidas</h2>
            <p>
              A Colônia de Férias atingiu o limite de {vagas.limite} inscrições confirmadas e não está mais recebendo
              novas inscrições.
            </p>
            <p className="inscricoes-encerradas-contato">
              Ficou com dúvida ou quer entrar na lista de espera? Fale com a secretaria do UBA Clube.
            </p>
            <button type="button" className="btn-voltar-inicio" onClick={() => navigate('/')}>
              Voltar ao início
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="uba-form-page">
      <div className="uba-container">
        <header className="uba-header">
          <img src="/logo-compact.webp" alt="Colônia de Férias - UBA Clube" className="uba-header-logo" />
        </header>

        {vagas && vagas.restantes <= 10 && (
          <div className="inscricoes-aviso-vagas">
            Últimas vagas: restam <strong>{vagas.restantes}</strong> de {vagas.limite}.
          </div>
        )}

        <>
            <ProgressSteps etapaAtual={etapaAtual} etapaCompleta={etapaCompleta} onStep={irParaEtapa} />
            <form className="contract-form" onSubmit={submit}>
              {etapaAtual === 1 && <DadosCriancaStep {...commonStepProps} handlePhoto={handlePhoto} handleDataNascimento={handleDataNascimento} handleCotaChange={handleCotaChange} handleNomeChange={handleNomeChange} />}
              {etapaAtual === 2 && <EnderecoStep {...commonStepProps} buscandoCep={buscandoCep} cepErro={cepErro} buscarCep={buscarCep} />}
              {etapaAtual === 3 && <CamisaStep {...commonStepProps} />}
              {etapaAtual === 4 && <ResponsaveisStep {...commonStepProps} telefoneErro={telefoneErro} validarTelefoneMae={validarTelefoneMae} />}
              {etapaAtual === 5 && <RetiradaStep {...commonStepProps} pessoasRetirada={pessoasRetirada} setPessoasRetirada={setPessoasRetirada} updatePessoaRetirada={updatePessoaRetirada} handlePhoto={handlePhoto} />}
              {etapaAtual === 6 && <InformacoesStep {...commonStepProps} />}
              {exibirAdicionais && etapaAtual === 7 && <AdicionaisStep {...commonStepProps} />}
              {etapaAtual === 8 && (
                <ConsentimentoStep
                  {...commonStepProps}
                  loading={loading}
                  assinaturaDesenhada={assinaturaDesenhada}
                  assinaturaErro={assinaturaErro || submitError}
                  assinaturaUrl={assinaturaUrl}
                  valorInscricao={valorInscricao}
                  valorAdicionais={valorAdicionais}
                  valorTotal={valorTotal}
                  setShowContratoModal={setShowContratoModal}
                  onSignatureChange={(drawn, dataUrl) => {
                    setAssinaturaDesenhada(drawn);
                    setAssinaturaErro('');
                    if (dataUrl !== undefined) setAssinaturaUrl(dataUrl);
                  }}
                />
              )}
            </form>
        </>

        {showContratoModal && <ContratoModal checked={form.consentimentoLido} onChecked={(checked) => updateForm('consentimentoLido', checked)} onClose={() => setShowContratoModal(false)} />}
        {showGratuito && <GratuitoModal onClose={() => setShowGratuito(false)} />}
        {checkoutOpen && (
          <PaymentCheckout
            form={form}
            baseValue={valorTotal}
            loading={loading}
            error={submitError}
            payment={checkoutPayment}
            onClose={() => !loading && setCheckoutOpen(false)}
            onPay={handleCheckoutPayment}
            onFreeRegistration={handleCouponFreeRegistration}
            onCheckPayment={handleCheckPayment}
          />
        )}
      </div>
    </div>
  );
};

export default Formulario;
